<template>
  <div class="layer-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <div class="form-group template-input name">
      <label for="widget-name">Widget Name</label>
      <input
        class="form-control"
        type="text"
        id="widget-name"
        maxlength="40"
        placeholder="Name"
        :disabled="isWidgetLockedByOverride"
        v-model="value.object.name"
      />
    </div>

    <div class="form-group template-input">
      <label for="widget-type">Widget Type</label>
      <select class="form-control" id="widget-type" v-model="value.object.time.clockType">
        <option :value="TIME_CLOCK_TYPE.DIGITAL">Digital Clock</option>
        <option :value="TIME_CLOCK_TYPE.ANALOG">Analog Clock</option>
      </select>
    </div>

    <div class="form-group template-input">
      <label for="timezone">Timezone</label>
      <select class="form-control" id="timezone" v-model="value.object.time.timezone">
        <option v-for="timezone in timezones" :key="timezone.value" :value="timezone.value">
          {{ timezone.label }}
        </option>
      </select>
    </div>

    <div class="font-color-container">
      <div class="font-container form-group template-input">
        <label for="font">Font</label>
        <FontSelector
          :value="value.object.time.textFont"
          :fonts="userFonts"
          @update:value="updateTimeFont"
        ></FontSelector>
      </div>

      <div class="color-section">
        <div class="color-buttons-container">
          <ColorPicker v-model="value.object.time.fontColor" :leftPosition="-166">
            <div class="color-button" title="Font Color">
              <i class="material-icons-outlined align-middle">text_fields</i>
              <div
                class="color-bar"
                :style="{ backgroundColor: value.object.time.fontColor }"
              ></div>
            </div>
          </ColorPicker>

          <ColorPicker v-model="value.object.time.backgroundColor" :leftPosition="-216">
            <div class="color-button" title="Background Color">
              <img class="logo w-165" src="@/assets/icon/Colors.svg" alt="Background Color" />
              <div
                class="color-bar"
                :style="{ backgroundColor: value.object.time.backgroundColor }"
              ></div>
            </div>
          </ColorPicker>

          <ColorPicker
            v-if="value.object.time.clockType === 'analog'"
            v-model="value.object.time.clockColor"
            :leftPosition="-266"
          >
            <div class="color-button" title="Clock Color">
              <i class="material-icons align-middle">watch_later</i>
              <div
                class="color-bar"
                :style="{ backgroundColor: value.object.time.clockColor }"
              ></div>
            </div>
          </ColorPicker>
        </div>
      </div>
    </div>

    <div class="form-group template-input text-size-container">
      <div class="form-sub-group">
        <label for="numbers-size">Numbers Size</label>
        <input
          type="number"
          class="form-control"
          id="numbers-size"
          min="1"
          max="100"
          :disabled="isWidgetLockedByOverride"
          v-model="numbersSize"
        />
      </div>
      <div
        v-if="
          value.object.time.clockType === 'digital' &&
          value.object.time.format === TIME_FORMAT.TWELVE
        "
        class="form-sub-group"
      >
        <label for="text-size">Text Size</label>
        <input
          type="number"
          class="form-control"
          id="text-size"
          min="1"
          max="100"
          :disabled="isWidgetLockedByOverride"
          v-model="textSize"
        />
      </div>
    </div>

    <div v-if="value.object.time.clockType === 'digital'" class="form-group template-input">
      <label for="time-format">Time Format</label>
      <select class="form-control" id="time-format" v-model="value.object.time.format">
        <option :value="TIME_FORMAT.TWELVE">12 hours</option>
        <option :value="TIME_FORMAT.TWENTY_FOUR">24 hours</option>
      </select>
    </div>
  </div>
</template>

<script>
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import FontSelector from '@/components/common/fontSelector/FontSelector.vue';
  import ColorPicker from '@/components/ColorPicker.vue';

  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';
  import { restrictToBounds } from '@/helpers/draggable';

  import { TIME_CLOCK_TYPE, TIMEZONES, TIME_FORMAT } from '@/constant/timeWidget.js';

  export default {
    name: 'TimeWidgetPanel',

    components: {
      LockIcon,
      VisibilityIcon,
      PixelIcon,
      FontSelector,
      ColorPicker,
    },

    mixins: [widgetMixin],

    data() {
      return {
        TIME_CLOCK_TYPE,
        timezones: TIMEZONES,
        TIME_FORMAT,
      };
    },

    computed: {
      userFonts() {
        return this.$store.getters.userFonts;
      },

      textSize: {
        get() {
          return this.value.object.time.textSize;
        },
        set(value) {
          const textSize = restrictToBounds(value, 1, 100);
          this.$set(this.value.object.time, 'textSize', Number(textSize));
        },
      },

      numbersSize: {
        get() {
          return this.value.object.time.numbersSize;
        },
        set(value) {
          const numbersSize = restrictToBounds(value, 1, 100);
          this.$set(this.value.object.time, 'numbersSize', Number(numbersSize));
        },
      },
    },

    methods: {
      updateTimeFont(newFont) {
        this.value.object.time.textFont = newFont;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .layer-widget-panel {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 0px;
    padding-right: 10px;

    .sub-section-title {
      padding-top: 4px;
      font-weight: 500;
      font-size: 14px;
      line-height: 24px;
      width: 100%;
    }
  }

  .buttons-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .color-buttons-container {
    display: flex;
    position: relative;
    align-items: flex-end;
    gap: 10px;
    min-width: 140px;
  }

  .font-color-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 1rem;

    .form-group {
      margin-bottom: 0;
      width: 100%;
    }

    .color-section {
      display: flex;
      position: relative;
      align-items: flex-end;
      gap: 10px;
      min-width: 140px;
    }
  }

  .text-size-container {
    display: flex;
    gap: 12px;

    .form-sub-group {
      flex: 1;
    }
  }
</style>
